import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	BesharpTableComponent,
	BesharpTableHeaderDefinition,
	BesharpTableSize,
} from '../../modules/besharp-table/component/besharp-table.component';
import { MessageService, ConfirmationService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { DeployService } from '../../services/deploy.service';
import { MenderService } from '../../services/mender.service';
import { formatMenderStatus } from '../../utils/mender-status.util';
import { fetchAuthSession } from 'aws-amplify/auth';

export type Deployment = {
	id: string;
	deploymentDate: string;
	notes: string;
	deploymentNotes: string;
	status: string;
	updateMode: string;
	machineId: string;
	softwareVersionId: string;
	userAccountId: string;
	serialNumber: string;
	family?: string;
	createdAt: string;
	updatedAt: string;
	deploymentStatus?: string;
	completionPercentage?: number;
	// Mender-specific fields
	isMender?: boolean;
	artifactName?: string;
	menderDeploymentId?: string;
};

export type ActiveTab = 'traditional' | 'mender';

@Component({
	standalone: true,
	imports: [
		CommonModule,
		CardModule,
		BesharpTableComponent,
		ButtonModule,
		ToastModule,
		DialogModule,
		FormsModule,
		ProgressSpinnerModule,
		SelectModule,
		ConfirmDialogModule,
		TooltipModule,
		ProgressBarModule,
	],
	providers: [MessageService, ConfirmationService],
	selector: 'app-deploy-management',
	templateUrl: './deploy-management.component.html',
	styleUrls: ['./deploy-management.component.scss'],
})
export class DeployManagementComponent implements OnInit, OnDestroy {
	eBesharpTableSize = BesharpTableSize;

	// ── Tab state ─────────────────────────────────────────────────────────────
	activeTab: ActiveTab = 'traditional';

	// ── Traditional tab state ─────────────────────────────────────────────────
	traditionalDatasource: Deployment[] = [];
	traditionalTotal = 0;
	traditionalOrderColumn = 'deployment_date';
	traditionalOrderDirection: 'asc' | 'desc' = 'desc';
	traditionalFilters: { [key: string]: any } = {};
	traditionalLoading = false;
	traditionalPageRows = 10;
	traditionalOffset = 0;

	// ── Mender tab state ──────────────────────────────────────────────────────
	menderDatasource: Deployment[] = [];
	menderTotal = 0;
	menderOrderColumn = 'deployment_date';
	menderOrderDirection: 'asc' | 'desc' = 'desc';
	menderFilters: { [key: string]: any } = {};
	menderLoading = false;
	menderPageRows = 10;
	menderOffset = 0;

	// ── Shared UI state ───────────────────────────────────────────────────────
	refreshLoading = false;

	// Modal properties
	showDetailsModal = false;
	loadingDetails = false;
	selectedDeployment: any = null;
	deploymentDetails: any = null;
	deploymentProgress: any = null;

	private userGroups: string[] = [];
	private canCancel = false;
	private deploymentPollingInterval: any = null;
	private readonly POLLING_REFRESH_MS = 5 * 60 * 1000; // 5 min

	private refreshTablesListener: ((event: Event) => void) | null = null;

	// ── Table definitions ─────────────────────────────────────────────────────

	traditionalTableDefinition: BesharpTableHeaderDefinition[] = [
		{
			def: 'deploymentDate',
			title: 'Deployment Date',
			sortable: true,
			columnFilter: false,
			transformer: (date: string) => this.formatDate(date),
		},
		{
			def: 'serialNumber',
			title: 'Serial Number',
			sortable: true,
			columnFilter: true,
			transformer: (serialNumber: string) => serialNumber || '-',
		},
		{
			def: 'notes',
			title: 'Artifact / Version',
			sortable: true,
			columnFilter: true,
			transformer: (notes: string) => {
				if (!notes) return '-';
				return notes.length > 50 ? notes.substring(0, 50) + '...' : notes;
			},
			tooltip: (row: any) => row.notes || '',
			tooltipPosition: 'top',
		},
		{
			def: 'deploymentNotes',
			title: 'Deployment Notes',
			sortable: false,
			columnFilter: false,
			transformer: (deploymentNotes: string) => {
				if (!deploymentNotes) return '-';
				return deploymentNotes.length > 50 ? deploymentNotes.substring(0, 50) + '...' : deploymentNotes;
			},
			tooltip: (row: any) => row.deploymentNotes || '',
			tooltipPosition: 'top',
		},
		{
			def: 'updateMode',
			title: 'Update Mode',
			sortable: true,
			columnFilter: true,
			virtual: (row: any) => (row.updateMode ? row.updateMode.toUpperCase() : '-'),
		},
		{
			def: 'deploymentStatus',
			title: 'Status',
			sortable: false,
			columnFilter: false,
			transformer: (value: string) => {
				if (value === 'LOADING_STATUS') {
					return '<i class="pi pi-spin pi-spinner loading-spinner"></i>';
				}
				return value || '-';
			},
		},
		{
			def: 'scheduledAt',
			title: 'Scheduled At',
			sortable: false,
			columnFilter: false,
			transformer: (date: string) => this.formatDate(date),
		},
	];

	menderTableDefinition: BesharpTableHeaderDefinition[] = [
		{
			def: 'deploymentDate',
			title: 'Deployment Date',
			sortable: true,
			columnFilter: false,
			transformer: (date: string) => this.formatDate(date),
		},
		{
			def: 'serialNumber',
			title: 'Serial Number',
			sortable: true,
			columnFilter: true,
			transformer: (serialNumber: string) => serialNumber || '-',
		},
		{
			def: 'artifactName',
			title: 'Artifact Name',
			sortable: true,
			columnFilter: false,
			transformer: (artifactName: string) => artifactName || '-',
		},
		{
			def: 'deploymentStatus',
			title: 'Status',
			sortable: true,
			columnFilter: false,
			transformer: (value: string) => value || '-',
		},
		{
			def: 'scheduledAt',
			title: 'Scheduled At',
			sortable: true,
			columnFilter: false,
			transformer: (date: string) => this.formatDate(date),
		},
	];

	globalFilterFieldsTraditional = this.traditionalTableDefinition.map((r) => r.def);
	globalFilterFieldsMender = this.menderTableDefinition.map((r) => r.def);

	actionDefinition = [
		{
			label: 'Details',
			icon: 'pi pi-info-circle',
			styleClass: 'b',
			callback: (row: any) => this.showDeploymentDetails(row),
		},
		{
			label: 'Cancel',
			icon: 'pi pi-times',
			styleClass: 'b',
			callback: (row: any) => this.confirmCancelDeployment(row),
			active: (row: any) => this.canCancelDeployment(row.status),
		},
	];

	// ── Lifecycle ─────────────────────────────────────────────────────────────

	constructor(
		protected router: Router,
		protected http: HttpClient,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private deployService: DeployService,
		private menderService: MenderService
	) {
		this.loadUserGroups();
		this.loadTraditional();
		this.loadMender();
	}

	ngOnInit(): void {
		this.setupRefreshListener();
		this.startDeploymentPolling();
	}

	ngOnDestroy(): void {
		this.removeRefreshListener();
		this.stopDeploymentPolling();
	}

	// ── Tab switching ─────────────────────────────────────────────────────────

	selectTab(tab: ActiveTab): void {
		if (this.activeTab === tab) return;
		this.activeTab = tab;
	}

	// ── Traditional data loading ──────────────────────────────────────────────

	loadTraditional(
		limit = this.traditionalPageRows,
		offset = this.traditionalOffset,
		orderColumn = this.traditionalOrderColumn,
		orderDirection: 'asc' | 'desc' = this.traditionalOrderDirection,
		filters = this.traditionalFilters
	): void {
		this.traditionalDatasource = [];
		this.traditionalLoading = true;

		this.deployService.getDeployments(limit, offset, orderColumn, orderDirection, filters).subscribe({
			next: (response) => {
				this.traditionalDatasource = (response.data || []).map((item: any) => ({
					...item,
					isMender: false,
					deploymentStatus: 'LOADING_STATUS',
				}));
				this.traditionalTotal = response.total || 0;
				this.traditionalLoading = false;
				this.loadTraditionalStatuses();
			},
			error: (error) => {
				this.traditionalLoading = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: `Failed to load deployments: ${error.message}`,
				});
			},
		});
	}

	private loadTraditionalStatuses(): void {
		if (this.traditionalDatasource.length === 0) return;

		const details = this.traditionalDatasource.map((d) => ({
			deploymentId: d.id,
			softwareVersionId: d.softwareVersionId,
		}));

		this.deployService.getDeploymentProgress(details).subscribe({
			next: (response) => {
				const items: any[] = Array.isArray(response) ? response : (response?.data || []);
				items.forEach((statusData: any) => {
					const deployment = this.traditionalDatasource.find((d) => d.id === statusData.deploymentId);
					if (deployment) {
						deployment.deploymentStatus = this.formatDeploymentStatus(
							statusData.status,
							statusData.completionPercentage
						);
						deployment.completionPercentage = statusData.completionPercentage;
					}
				});
				this.traditionalDatasource = [...this.traditionalDatasource];
			},
			error: () => {
				this.traditionalDatasource.forEach((d) => {
					if (d.deploymentStatus === 'LOADING_STATUS') d.deploymentStatus = '-';
				});
				this.traditionalDatasource = [...this.traditionalDatasource];
			},
		});
	}

	// ── Mender data loading ───────────────────────────────────────────────────

	loadMender(
		limit = this.menderPageRows,
		offset = this.menderOffset,
		orderColumn = this.menderOrderColumn,
		orderDirection: 'asc' | 'desc' = this.menderOrderDirection,
		filters = this.menderFilters
	): void {
		this.menderDatasource = [];
		this.menderLoading = true;

		const extractFilter = (key: string): string | undefined => {
			const raw = filters?.[key];
			if (!raw) return undefined;
			if (Array.isArray(raw)) {
				const val = raw[0]?.value;
				return val != null && val !== '' ? String(val) : undefined;
			}
			return raw !== null && raw !== '' ? String(raw) : undefined;
		};

		this.menderService
			.listDeployments({
				serialNumber: extractFilter('serialNumber'),
				search: extractFilter('search'),
				status: extractFilter('status'),
				limit,
				offset,
				orderColumn,
				orderDirection,
			})
			.subscribe({
				next: (response) => {
					this.menderDatasource = (response.data || []).map((item: any) => ({
						id: item.id,
						deploymentDate: item.deploymentDate,
						notes: item.notes,
						deploymentNotes: item.deploymentNotes,
						status: item.status,
						updateMode: 'MENDER',
						machineId: item.machineId,
						softwareVersionId: '',
						userAccountId: item.userAccountId,
						serialNumber: item.serialNumber,
						family: item.family,
						createdAt: item.createdAt,
						updatedAt: item.updatedAt,
						scheduledAt: item.scheduledAt,
						isMender: true,
						artifactName: item.artifactName,
						menderDeploymentId: item.menderDeploymentId,
						menderSerial: item.menderSerial,
						artifactDeviceType: item.artifactDeviceType,
						deploymentStatus: formatMenderStatus(item.status),
					}));
					this.menderTotal = response.total || 0;
					this.menderLoading = false;
					this.loadMenderStatuses();
				},
				error: (error) => {
					this.menderLoading = false;
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: `Failed to load Mender deployments: ${error.message}`,
					});
				},
			});
	}

	private loadMenderStatuses(): void {
		if (this.menderDatasource.length === 0) return;

		const terminalStatuses = ['COMPLETED', 'ALREADY_INSTALLED', 'ERROR', 'CANCELED', 'SCHEDULED'];

		this.menderDatasource.forEach((d) => {
			if (terminalStatuses.includes(d.status?.toUpperCase())) return;
			if (!d.menderDeploymentId) return;

			this.menderService.getDeploymentStatus(d.menderDeploymentId, d.id).subscribe({
				next: (response) => {
					const target = this.menderDatasource.find((row) => row.id === d.id);
					if (target) {
						target.status = response.status;
						target.deploymentStatus = formatMenderStatus(response.status);
					}
					this.menderDatasource = [...this.menderDatasource];
				},
				error: () => { /* keep the DB status on error */ },
			});
		});
	}

	// ── Polling ───────────────────────────────────────────────────────────────

	private startDeploymentPolling(): void {
		this.deploymentPollingInterval = setInterval(() => {
			if (this.activeTab === 'traditional') {
				this.loadTraditionalStatuses();
			} else {
				this.loadMenderStatuses();
			}
		}, this.POLLING_REFRESH_MS);
	}

	private stopDeploymentPolling(): void {
		if (this.deploymentPollingInterval) {
			clearInterval(this.deploymentPollingInterval);
			this.deploymentPollingInterval = null;
		}
	}

	// ── Refresh listener ──────────────────────────────────────────────────────

	private setupRefreshListener(): void {
		this.refreshTablesListener = () => {
			this.loadTraditional();
			this.loadMender();
		};
		window.addEventListener('refreshTable', this.refreshTablesListener);
	}

	private removeRefreshListener(): void {
		if (this.refreshTablesListener) {
			window.removeEventListener('refreshTable', this.refreshTablesListener);
		}
	}

	// ── Traditional tab event handlers ────────────────────────────────────────

	onTraditionalPageChange(event: any): void {
		this.traditionalPageRows = event.rows;
		this.traditionalOffset = event.first;
		this.loadTraditional(
			this.traditionalPageRows,
			this.traditionalOffset,
			this.traditionalOrderColumn,
			this.traditionalOrderDirection,
			this.traditionalFilters
		);
	}

	onTraditionalSortChange(event: any): void {
		this.traditionalOrderColumn = this.mapFieldToBackend(event.field);
		this.traditionalOrderDirection = event.order === 1 ? 'asc' : 'desc';
		this.traditionalOffset = 0;
		this.loadTraditional(
			this.traditionalPageRows,
			this.traditionalOffset,
			this.traditionalOrderColumn,
			this.traditionalOrderDirection,
			this.traditionalFilters
		);
	}

	onTraditionalColumnFilter(event: any): void {
		this.traditionalFilters = event.filters || {};
		this.loadTraditional(
			this.traditionalPageRows,
			0,
			this.traditionalOrderColumn,
			this.traditionalOrderDirection,
			this.traditionalFilters
		);
	}

	onTraditionalGlobalSearch(searchValue: string): void {
		this.traditionalFilters = { ...this.traditionalFilters, search: searchValue || null };
		this.loadTraditional(
			this.traditionalPageRows,
			0,
			this.traditionalOrderColumn,
			this.traditionalOrderDirection,
			this.traditionalFilters
		);
	}

	clearTraditionalFilters(): void {
		this.traditionalOrderColumn = 'deployment_date';
		this.traditionalOrderDirection = 'desc';
		this.traditionalFilters = {};
		this.traditionalOffset = 0;
		this.loadTraditional(
			this.traditionalPageRows,
			this.traditionalOffset,
			this.traditionalOrderColumn,
			this.traditionalOrderDirection,
			this.traditionalFilters
		);
	}

	// ── Mender tab event handlers ─────────────────────────────────────────────

	onMenderPageChange(event: any): void {
		this.menderPageRows = event.rows;
		this.menderOffset = event.first;
		this.loadMender(
			this.menderPageRows,
			this.menderOffset,
			this.menderOrderColumn,
			this.menderOrderDirection,
			this.menderFilters
		);
	}

	onMenderSortChange(event: any): void {
		this.menderOrderColumn = this.mapFieldToBackend(event.field);
		this.menderOrderDirection = event.order === 1 ? 'asc' : 'desc';
		this.menderOffset = 0;
		this.loadMender(
			this.menderPageRows,
			this.menderOffset,
			this.menderOrderColumn,
			this.menderOrderDirection,
			this.menderFilters
		);
	}

	onMenderColumnFilter(event: any): void {
		this.menderFilters = event.filters || {};
		this.loadMender(
			this.menderPageRows,
			0,
			this.menderOrderColumn,
			this.menderOrderDirection,
			this.menderFilters
		);
	}

	onMenderGlobalSearch(searchValue: string): void {
		this.menderFilters = { ...this.menderFilters, search: searchValue || null };
		this.loadMender(
			this.menderPageRows,
			0,
			this.menderOrderColumn,
			this.menderOrderDirection,
			this.menderFilters
		);
	}

	clearMenderFilters(): void {
		this.menderOrderColumn = 'deployment_date';
		this.menderOrderDirection = 'desc';
		this.menderFilters = {};
		this.menderOffset = 0;
		this.loadMender(
			this.menderPageRows,
			this.menderOffset,
			this.menderOrderColumn,
			this.menderOrderDirection,
			this.menderFilters
		);
	}

	// ── Refresh button ────────────────────────────────────────────────────────

	refreshDeploymentProgress(): void {
		this.refreshLoading = true;

		if (this.activeTab === 'traditional') {
			this.loadTraditional(
				this.traditionalPageRows,
				this.traditionalOffset,
				this.traditionalOrderColumn,
				this.traditionalOrderDirection,
				this.traditionalFilters
			);
			this.refreshLoading = false;
		} else {
			this.loadMender(
				this.menderPageRows,
				this.menderOffset,
				this.menderOrderColumn,
				this.menderOrderDirection,
				this.menderFilters
			);
			this.refreshLoading = false;
		}
	}

	// ── Deployment details modal ───────────────────────────────────────────────

	showDeploymentDetails(deployment: any): void {
		this.selectedDeployment = deployment;
		this.showDetailsModal = true;
		this.loadingDetails = true;
		this.deploymentDetails = null;
		this.deploymentProgress = null;

		if (deployment.isMender) {
			const base = {
				status: deployment.status,
				deploymentDate: deployment.deploymentDate,
				serialNumber: deployment.serialNumber,
				family: deployment.family,
				artifactName: deployment.artifactName,
				menderDeploymentId: deployment.menderDeploymentId,
				menderSerial: deployment.menderSerial,
				artifactDeviceType: deployment.artifactDeviceType,
				deploymentNotes: deployment.deploymentNotes,
				notes: deployment.notes,
				isMender: true,
				liveDetails: null as any,
			};

			if (deployment.menderDeploymentId) {
				this.menderService.getDeploymentDetails(deployment.menderDeploymentId).subscribe({
					next: (details) => {
						base.liveDetails = details;
						this.deploymentDetails = { ...base };
						this.loadingDetails = false;
					},
					error: () => {
						this.deploymentDetails = { ...base };
						this.loadingDetails = false;
					},
				});
			} else {
				this.deploymentDetails = base;
				this.loadingDetails = false;
			}
			return;
		}

		// Traditional deployment
		const detailsRequest = this.deployService.getDeploymentDetails(deployment.id);
		const progressRequest = this.deployService.getDeploymentProgress([
			{ deploymentId: deployment.id, softwareVersionId: deployment.softwareVersionId },
		]);

		detailsRequest.subscribe({
			next: (details) => {
				this.deploymentDetails = details;
				this.checkLoadingComplete();
			},
			error: (error) => {
				console.error('Error fetching deployment details:', error);
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to fetch deployment details',
				});
				this.checkLoadingComplete();
			},
		});

		const isActive =
			deployment.status?.toLowerCase() === 'pending' ||
			deployment.status?.toLowerCase() === 'in_progress' ||
			deployment.status?.toLowerCase() === 'in-progress';

		if (isActive) {
			progressRequest.subscribe({
				next: (progress) => {
					this.deploymentProgress = progress.data || progress;
					this.checkLoadingComplete();
				},
				error: (error) => {
					console.error('Error fetching deployment progress:', error);
					this.checkLoadingComplete();
				},
			});
		} else {
			this.checkLoadingComplete();
		}
	}

	private checkLoadingComplete(): void {
		const isDetailsLoaded = this.deploymentDetails !== null;
		const isProgressRelevant = !(
			this.selectedDeployment?.status?.toLowerCase() === 'pending' ||
			this.selectedDeployment?.status?.toLowerCase() === 'in_progress' ||
			this.selectedDeployment?.status?.toLowerCase() === 'in-progress'
		);
		const isProgressLoaded = this.deploymentProgress !== null || isProgressRelevant;

		if (isDetailsLoaded && isProgressLoaded) {
			this.loadingDetails = false;
		}
	}

	closeDetailsModal(): void {
		this.showDetailsModal = false;
		this.selectedDeployment = null;
		this.deploymentDetails = null;
		this.deploymentProgress = null;
		this.loadingDetails = false;
	}

	// ── Cancel deployment ─────────────────────────────────────────────────────

	canCancelDeployment(status: string): boolean {
		const noCancelStatuses = ['done', 'completed', 'error', 'cancel', 'canceled'];
		if (noCancelStatuses.includes(status?.toLowerCase())) return false;
		return this.canCancel;
	}

	confirmCancelDeployment(deployment: any): void {
		this.confirmationService.confirm({
			message: `Are you sure you want to cancel the deployment for machine <strong>${deployment.serialNumber}</strong>?`,
			header: 'Cancel Deployment',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: 'Yes',
			rejectLabel: 'No',
			acceptButtonStyleClass: 'p-button-danger p-button-text',
			rejectButtonStyleClass: 'p-button-text p-button-secondary',
			defaultFocus: 'reject',
			accept: () => this.cancelDeployment(deployment),
		});
	}

	cancelDeployment(deployment?: any): void {
		const target = deployment || this.selectedDeployment;
		if (!target) return;

		const cancel$ = target.isMender
			? this.menderService.cancelDeployment(target.id)
			: this.deployService.cancelDeployment(target.id);

		cancel$.subscribe({
			next: () => {
				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: 'Deployment canceled',
				});
				this.selectedDeployment = null;
				if (target.isMender) {
					this.loadMender();
				} else {
					this.loadTraditional();
				}
			},
			error: (error) => {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: error.error?.message || 'Failed to cancel deployment. Please try again.',
				});
				this.selectedDeployment = null;
			},
		});
	}

	// ── Helpers ───────────────────────────────────────────────────────────────

	formatDeploymentStatus(status: string, percentage?: number): string {
		switch (status?.toLowerCase()) {
			case 'done':
				return 'Done';
			case 'cancel':
				return 'Canceled';
			case 'pending':
				return 'Pending';
			case 'running':
				return `${Math.round(percentage || 0)}%`;
			case 'starting':
				return 'Starting';
			case 'completed':
				return 'Done';
			case 'error':
				return 'Error';
			case 'scheduled':
				return 'Scheduled';
			default:
				return '-';
		}
	}

	formatDate(dateString: string): string {
		if (!dateString) return '-';
		const d = new Date(dateString);
		return d.toLocaleDateString('en-GB', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		});
	}

	getStatusBadge(status: string): string {
		if (!status) return '-';
		const statusClass =
			status.toLowerCase() === 'completed'
				? 'success'
				: status.toLowerCase() === 'failed'
					? 'danger'
					: status.toLowerCase() === 'in_progress'
						? 'warning'
						: 'info';
		return `<span class="badge badge-${statusClass}">${status.replace('_', ' ')}</span>`;
	}

	private mapFieldToBackend(field: string): string {
		const mapping: Record<string, string> = {
			id: 'id',
			deploymentDate: 'deployment_date',
			serialNumber: 'serial_number',
			notes: 'notes',
			deploymentNotes: 'deployment_notes',
			status: 'status',
			updateMode: 'update_mode',
			machineId: 'machine_id',
			softwareVersionId: 'software_version_id',
			userAccountId: 'user_account_id',
			createdAt: 'created_at',
			updatedAt: 'updated_at',
			scheduledAt: 'scheduled_at',
			artifactName: 'artifact_name',
			deploymentStatus: 'status',
		};
		return mapping[field] || field;
	}

	private async loadUserGroups(): Promise<void> {
		try {
			const session = await fetchAuthSession();
			const idToken = session.tokens?.idToken;
			if (idToken) {
				const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
				this.userGroups = payload['cognito:groups'] || [];
				const isReadOnly = this.userGroups.some(
					(group) => group.includes('global-admin') || group.includes('sw-manager')
				);
				this.canCancel = !isReadOnly;
			}
		} catch (error) {
			console.error('Error loading user groups:', error);
			this.canCancel = false;
		}
	}
}
